// src/pages/PriceQuotes/NewPriceQuote.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, addDoc, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

// --- STYLES ---
const styles = {
    container: { fontFamily: '"Inter", sans-serif', maxWidth: '1100px', margin: '0 auto', color: '#1f2937' },
    header: { marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' },
    title: { fontSize: '28px', fontWeight: '800', color: '#111827', margin: 0, letterSpacing: '-0.5px' },
    subtitle: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
    backButton: {
        background: 'none', border: '1px solid #e2e8f0', color: '#6b7280', cursor: 'pointer',
        fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px',
        padding: '10px 20px', borderRadius: '10px', transition: 'all 0.2s'
    },

    box: {
        background: 'white', padding: '32px', borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9', marginBottom: '24px'
    },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '28px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: {
        padding: '12px 16px', border: '1px solid #e2e8f0', borderRadius: '10px',
        fontSize: '14px', width: '100%', boxSizing: 'border-box', outline: 'none',
        transition: 'all 0.2s', height: '46px', backgroundColor: '#f8fafc'
    },
    select: {
        padding: '12px 16px', border: '1px solid #e2e8f0', borderRadius: '10px',
        fontSize: '14px', width: '100%', boxSizing: 'border-box', outline: 'none',
        backgroundColor: '#f8fafc', height: '46px', appearance: 'none',
        backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
        backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '14px'
    },
    textarea: {
        padding: '12px 16px', border: '1px solid #e2e8f0', borderRadius: '10px',
        fontSize: '14px', width: '100%', boxSizing: 'border-box', outline: 'none',
        transition: 'all 0.2s', backgroundColor: '#f8fafc', resize: 'vertical',
        minHeight: '80px', fontFamily: '"Inter", sans-serif'
    },

    sectionHeader: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '16px', borderBottom: '2px solid #f1f5f9', paddingBottom: '12px'
    },
    sectionTitle: { fontSize: '16px', fontWeight: '700', color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' },

    productRow: {
        display: 'grid', gridTemplateColumns: '2.5fr 1fr 1fr 1fr 1fr auto',
        gap: '12px', alignItems: 'end', background: '#f8fafc', padding: '16px',
        borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '16px'
    },
    addBtn: {
        padding: '10px 20px', border: 'none', borderRadius: '10px', cursor: 'pointer',
        fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center',
        gap: '6px', background: '#3b82f6', color: 'white', height: '46px',
        transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(59,130,246,0.2)'
    },

    tableContainer: { border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', marginBottom: '24px' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
        background: '#f8fafc', padding: '12px 14px', textAlign: 'left',
        borderBottom: '1px solid #e5e7eb', fontSize: '11px', fontWeight: '600',
        color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px'
    },
    td: { padding: '14px', borderBottom: '1px solid #f3f4f6', fontSize: '14px', color: '#1f2937' },
    deleteBtn: {
        background: 'white', border: '1px solid #fee2e2', color: '#ef4444', cursor: 'pointer',
        padding: '6px', borderRadius: '6px', display: 'flex', alignItems: 'center',
        justifyContent: 'center', transition: 'all 0.2s', width: '32px', height: '32px'
    },
    emptyState: { padding: '48px', textAlign: 'center', color: '#9ca3af', fontStyle: 'italic', background: '#fafafa', borderRadius: '8px' },

    totalsWrapper: { display: 'flex', justifyContent: 'flex-end', marginTop: '24px' },
    totalsBox: { width: '380px', padding: '24px', background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)', borderRadius: '16px', border: '1px solid #e2e8f0' },
    totalRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '14px', color: '#4b5563', alignItems: 'center' },
    totalLabel: { fontWeight: '500' },
    grandTotalRow: { display: 'flex', justifyContent: 'space-between', marginTop: '14px', paddingTop: '14px', borderTop: '2px solid #cbd5e1' },
    grandTotalLabel: { fontSize: '16px', fontWeight: '700', color: '#111827' },
    grandTotalValue: { fontSize: '22px', fontWeight: '800', color: '#059669' },

    saveButton: {
        padding: '14px 40px', border: 'none', borderRadius: '12px', cursor: 'pointer',
        fontSize: '16px', fontWeight: '700', display: 'flex', alignItems: 'center',
        gap: '8px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white',
        boxShadow: '0 4px 15px rgba(16,185,129,0.3)', transition: 'all 0.3s ease'
    },
    inlineInput: {
        padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px',
        fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box',
        backgroundColor: '#fff', textAlign: 'right'
    },
    // Customer search autocomplete
    autocompleteWrapper: { position: 'relative' },
    autocompleteDropdown: {
        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
        background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: '220px', overflowY: 'auto',
        marginTop: '4px'
    },
    autocompleteItem: {
        padding: '10px 16px', cursor: 'pointer', fontSize: '14px',
        borderBottom: '1px solid #f3f4f6', transition: 'background 0.15s'
    },
    autocompleteEmpty: {
        padding: '12px 16px', fontSize: '13px', color: '#9ca3af', fontStyle: 'italic'
    },
    selectedBadge: {
        display: 'inline-flex', alignItems: 'center', gap: '8px',
        background: '#ede9fe', color: '#6d28d9', padding: '8px 14px',
        borderRadius: '10px', fontSize: '14px', fontWeight: '500'
    },
    clearBtn: {
        background: 'none', border: 'none', cursor: 'pointer', color: '#6d28d9',
        fontSize: '16px', padding: '0 2px', lineHeight: 1
    }
};

function NewPriceQuote() {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const companyId = userData?.companyId;

    // Data
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);

    // Form
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [customerSearch, setCustomerSearch] = useState('');
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const customerSearchRef = useRef(null);
    const [quoteDate, setQuoteDate] = useState(new Date().toISOString().split('T')[0]);
    const [validUntil, setValidUntil] = useState('');
    const [currency, setCurrency] = useState('TRY');
    const [notes, setNotes] = useState('');
    const [vatRatePercent, setVatRatePercent] = useState(20);
    const [generalDiscountPercent, setGeneralDiscountPercent] = useState(0);

    // Line Items
    const [lineItems, setLineItems] = useState([]);
    const [currentItem, setCurrentItem] = useState({ productId: '', quantity: 1, unitPrice: '', discount: 0 });
    const [productSearch, setProductSearch] = useState('');
    const [showProductDropdown, setShowProductDropdown] = useState(false);

    const [saving, setSaving] = useState(false);

    // Set default validity (30 days from now)
    useEffect(() => {
        const d = new Date();
        d.setDate(d.getDate() + 30);
        setValidUntil(d.toISOString().split('T')[0]);
    }, []);

    // Fetch data
    useEffect(() => {
        if (!companyId) return;
        const unsubCustomers = onSnapshot(
            query(collection(db, 'companies', companyId, 'customers')),
            snap => setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
        const unsubProducts = onSnapshot(
            query(collection(db, 'companies', companyId, 'products')),
            snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
        return () => { unsubCustomers(); unsubProducts(); };
    }, [companyId]);

    // When product changes, auto-fill price
    useEffect(() => {
        if (currentItem.productId) {
            const product = products.find(p => p.id === currentItem.productId);
            if (product) {
                setCurrentItem(prev => ({ ...prev, unitPrice: product.salePrice || 0 }));
            }
        }
    }, [currentItem.productId, products]);

    const getCurrencySymbol = () => {
        if (currency === 'USD') return '$';
        if (currency === 'EUR') return '€';
        return '₺';
    };

    const handleAddItem = () => {
        if (!currentItem.productId || currentItem.quantity <= 0) return;
        const product = products.find(p => p.id === currentItem.productId);
        if (!product) return;

        const qty = Number(currentItem.quantity);
        const price = Number(currentItem.unitPrice) || 0;
        const disc = Number(currentItem.discount) || 0;
        const lineTotal = qty * price * (1 - disc / 100);

        setLineItems(prev => [...prev, {
            productId: product.id,
            name: product.name,
            quantity: qty,
            unitPrice: price,
            discount: disc,
            total: lineTotal
        }]);
        setCurrentItem({ productId: '', quantity: 1, unitPrice: '', discount: 0 });
        setProductSearch('');
    };

    const handleRemoveItem = (index) => {
        setLineItems(prev => prev.filter((_, i) => i !== index));
    };

    const handleLineItemChange = (index, field, value) => {
        setLineItems(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: Number(value) || 0 };
            // Recalculate total
            const item = updated[index];
            item.total = item.quantity * item.unitPrice * (1 - (item.discount || 0) / 100);
            return updated;
        });
    };

    // Calculations
    const subTotal = lineItems.reduce((acc, item) => acc + item.total, 0);
    const generalDiscountAmount = subTotal * (generalDiscountPercent / 100);
    const afterDiscount = subTotal - generalDiscountAmount;
    const vatAmount = afterDiscount * (vatRatePercent / 100);
    const grandTotal = afterDiscount + vatAmount;

    const generateQuoteNumber = async () => {
        try {
            const q = query(
                collection(db, 'companies', companyId, 'priceQuotes'),
                orderBy('createdAt', 'desc'),
                limit(1)
            );
            const snap = await getDocs(q);
            if (snap.empty) return 'TKF-001';
            const lastQuote = snap.docs[0].data();
            const lastNumber = lastQuote.quoteNumber
                ? parseInt(lastQuote.quoteNumber.replace('TKF-', '')) || 0
                : 0;
            return `TKF-${String(lastNumber + 1).padStart(3, '0')}`;
        } catch {
            return `TKF-${Date.now().toString().slice(-4)}`;
        }
    };

    const handleSave = async () => {
        if (!selectedCustomer) {
            alert('Lütfen bir müşteri seçin.');
            return;
        }
        if (lineItems.length === 0) {
            alert('Lütfen en az bir ürün ekleyin.');
            return;
        }

        setSaving(true);
        try {
            const customer = customers.find(c => c.id === selectedCustomer);
            const quoteNumber = await generateQuoteNumber();

            await addDoc(collection(db, 'companies', companyId, 'priceQuotes'), {
                quoteNumber,
                customerId: customer.id,
                customerName: customer.name,
                customerAddress: customer.address || '',
                customerTaxOffice: customer.taxOffice || '',
                customerTaxNumber: customer.taxNumber || '',
                quoteDate: Timestamp.fromDate(new Date(quoteDate)),
                validUntil: Timestamp.fromDate(new Date(validUntil)),
                currency,
                lineItems,
                subTotal,
                discountRate: generalDiscountPercent,
                discountAmount: generalDiscountAmount,
                vatRate: vatRatePercent,
                vatAmount,
                total: grandTotal,
                notes,
                status: 'Taslak',
                createdAt: Timestamp.now(),
                createdBy: userData?.email || ''
            });

            navigate('/fiyat-teklifleri');
        } catch (error) {
            console.error('Teklif kaydedilirken hata:', error);
            alert('Teklif kaydedilirken bir hata oluştu.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Yeni Fiyat Teklifi</h1>
                    <p style={styles.subtitle}>Müşterinize göndermek üzere yeni bir fiyat teklifi oluşturun</p>
                </div>
                <button onClick={() => navigate('/fiyat-teklifleri')} style={styles.backButton}>
                    ← Tekliflere Dön
                </button>
            </div>

            {/* Genel Bilgiler */}
            <div style={styles.box}>
                <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>📝 Genel Bilgiler</h3>
                </div>
                <div style={styles.grid}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Müşteri</label>
                        {selectedCustomer ? (
                            <div style={styles.selectedBadge}>
                                <span>👤 {customers.find(c => c.id === selectedCustomer)?.name || 'Müşteri'}</span>
                                <button style={styles.clearBtn} onClick={() => { setSelectedCustomer(''); setCustomerSearch(''); }} title="Değiştir">✕</button>
                            </div>
                        ) : (
                            <div style={styles.autocompleteWrapper} ref={customerSearchRef}>
                                <input
                                    type="text"
                                    style={styles.input}
                                    placeholder="🔍 Müşteri adı yazarak arayın..."
                                    value={customerSearch}
                                    onChange={e => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                                    onFocus={() => setShowCustomerDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                                />
                                {showCustomerDropdown && customerSearch.length > 0 && (
                                    <div style={styles.autocompleteDropdown}>
                                        {customers.filter(c => c.name?.toLowerCase().includes(customerSearch.toLowerCase())).length === 0 ? (
                                            <div style={styles.autocompleteEmpty}>Müşteri bulunamadı</div>
                                        ) : (
                                            customers.filter(c => c.name?.toLowerCase().includes(customerSearch.toLowerCase())).map(c => (
                                                <div
                                                    key={c.id}
                                                    style={styles.autocompleteItem}
                                                    onMouseDown={() => { setSelectedCustomer(c.id); setCustomerSearch(c.name); setShowCustomerDropdown(false); }}
                                                    onMouseEnter={e => e.target.style.backgroundColor = '#f5f3ff'}
                                                    onMouseLeave={e => e.target.style.backgroundColor = 'white'}
                                                >
                                                    👤 {c.name}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Teklif Tarihi</label>
                        <input type="date" style={styles.input} value={quoteDate} onChange={e => setQuoteDate(e.target.value)} />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Geçerlilik Tarihi</label>
                        <input type="date" style={styles.input} value={validUntil} onChange={e => setValidUntil(e.target.value)} />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Para Birimi</label>
                        <select style={styles.select} value={currency} onChange={e => setCurrency(e.target.value)}>
                            <option value="TRY">₺ TRY (Türk Lirası)</option>
                            <option value="USD">$ USD (Amerikan Doları)</option>
                            <option value="EUR">€ EUR (Euro)</option>
                        </select>
                    </div>
                </div>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Notlar</label>
                    <textarea
                        style={styles.textarea}
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        placeholder="Teklif ile ilgili özel notlarınızı buraya yazabilirsiniz..."
                    />
                </div>
            </div>

            {/* Ürünler */}
            <div style={styles.box}>
                <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>📦 Ürünler</h3>
                </div>

                {/* Add Product Row */}
                <div style={styles.productRow}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Ürün</label>
                        {currentItem.productId ? (
                            <div style={{ ...styles.selectedBadge, background: '#ecfdf5', color: '#047857' }}>
                                <span>📦 {products.find(p => p.id === currentItem.productId)?.name || 'Ürün'}</span>
                                <button style={{ ...styles.clearBtn, color: '#047857' }} onClick={() => { setCurrentItem(prev => ({ ...prev, productId: '', unitPrice: '' })); setProductSearch(''); }} title="Değiştir">✕</button>
                            </div>
                        ) : (
                            <div style={styles.autocompleteWrapper}>
                                <input
                                    type="text"
                                    style={styles.input}
                                    placeholder="🔍 Ürün adı yazarak arayın..."
                                    value={productSearch}
                                    onChange={e => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                                    onFocus={() => setShowProductDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                                />
                                {showProductDropdown && productSearch.length > 0 && (
                                    <div style={styles.autocompleteDropdown}>
                                        {products.filter(p => p.name?.toLowerCase().includes(productSearch.toLowerCase())).length === 0 ? (
                                            <div style={styles.autocompleteEmpty}>Ürün bulunamadı</div>
                                        ) : (
                                            products.filter(p => p.name?.toLowerCase().includes(productSearch.toLowerCase())).map(p => (
                                                <div
                                                    key={p.id}
                                                    style={styles.autocompleteItem}
                                                    onMouseDown={() => {
                                                        setCurrentItem(prev => ({ ...prev, productId: p.id, unitPrice: p.salePrice || 0 }));
                                                        setProductSearch('');
                                                        setShowProductDropdown(false);
                                                    }}
                                                    onMouseEnter={e => e.target.style.backgroundColor = '#f0fdf4'}
                                                    onMouseLeave={e => e.target.style.backgroundColor = 'white'}
                                                >
                                                    📦 {p.name} {p.salePrice ? <span style={{ color: '#6b7280', marginLeft: '8px' }}>{p.salePrice} {getCurrencySymbol()}</span> : ''}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Miktar</label>
                        <input
                            type="number" min="1" style={styles.input}
                            value={currentItem.quantity}
                            onChange={e => setCurrentItem(prev => ({ ...prev, quantity: e.target.value }))}
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Birim Fiyat ({getCurrencySymbol()})</label>
                        <input
                            type="number" min="0" step="0.01" style={styles.input}
                            value={currentItem.unitPrice}
                            onChange={e => setCurrentItem(prev => ({ ...prev, unitPrice: e.target.value }))}
                            placeholder="0.00"
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>İskonto (%)</label>
                        <input
                            type="number" min="0" max="100" style={styles.input}
                            value={currentItem.discount}
                            onChange={e => setCurrentItem(prev => ({ ...prev, discount: e.target.value }))}
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Tutar</label>
                        <div style={{ ...styles.input, display: 'flex', alignItems: 'center', color: '#059669', fontWeight: '600', backgroundColor: '#f0fdf4' }}>
                            {currentItem.unitPrice
                                ? `${(Number(currentItem.quantity) * Number(currentItem.unitPrice) * (1 - Number(currentItem.discount) / 100)).toFixed(2)} ${getCurrencySymbol()}`
                                : `0.00 ${getCurrencySymbol()}`
                            }
                        </div>
                    </div>
                    <button onClick={handleAddItem} style={styles.addBtn} title="Ekle">
                        + Ekle
                    </button>
                </div>

                {/* Items Table */}
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>#</th>
                                <th style={styles.th}>Ürün Adı</th>
                                <th style={styles.th} width="90">Miktar</th>
                                <th style={styles.th} width="120">Birim Fiyat</th>
                                <th style={styles.th} width="80">İskonto</th>
                                <th style={styles.th} width="130" align="right">Toplam</th>
                                <th style={styles.th} width="50"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={styles.emptyState}>
                                        Henüz ürün eklenmedi. Yukarıdan ürün ekleyebilirsiniz.
                                    </td>
                                </tr>
                            ) : (
                                lineItems.map((item, index) => (
                                    <tr key={index}>
                                        <td style={{ ...styles.td, color: '#9ca3af', fontWeight: '500' }}>{index + 1}</td>
                                        <td style={{ ...styles.td, fontWeight: '500' }}>{item.name}</td>
                                        <td style={styles.td}>
                                            <input
                                                type="number" min="1"
                                                style={styles.inlineInput}
                                                value={item.quantity}
                                                onChange={e => handleLineItemChange(index, 'quantity', e.target.value)}
                                            />
                                        </td>
                                        <td style={styles.td}>
                                            <input
                                                type="number" min="0" step="0.01"
                                                style={styles.inlineInput}
                                                value={item.unitPrice}
                                                onChange={e => handleLineItemChange(index, 'unitPrice', e.target.value)}
                                            />
                                        </td>
                                        <td style={styles.td}>
                                            <input
                                                type="number" min="0" max="100"
                                                style={{ ...styles.inlineInput, width: '60px' }}
                                                value={item.discount}
                                                onChange={e => handleLineItemChange(index, 'discount', e.target.value)}
                                            />
                                        </td>
                                        <td style={{ ...styles.td, fontWeight: '600', textAlign: 'right' }}>
                                            {item.total.toFixed(2)} {getCurrencySymbol()}
                                        </td>
                                        <td style={styles.td}>
                                            <button onClick={() => handleRemoveItem(index)} style={styles.deleteBtn} title="Sil">
                                                🗑️
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Totals */}
                <div style={styles.totalsWrapper}>
                    <div style={styles.totalsBox}>
                        <div style={styles.totalRow}>
                            <span style={styles.totalLabel}>Ara Toplam:</span>
                            <span style={{ fontWeight: '600' }}>{subTotal.toFixed(2)} {getCurrencySymbol()}</span>
                        </div>
                        <div style={styles.totalRow}>
                            <span style={{ ...styles.totalLabel, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                Genel İskonto:
                                <input
                                    type="number" min="0" max="100"
                                    style={{ ...styles.inlineInput, width: '60px', textAlign: 'center' }}
                                    value={generalDiscountPercent}
                                    onChange={e => setGeneralDiscountPercent(Number(e.target.value) || 0)}
                                />
                                <span style={{ color: '#9ca3af' }}>%</span>
                            </span>
                            <span style={{ color: '#ef4444', fontWeight: '500' }}>
                                -{generalDiscountAmount.toFixed(2)} {getCurrencySymbol()}
                            </span>
                        </div>
                        <div style={styles.totalRow}>
                            <span style={{ ...styles.totalLabel, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                KDV:
                                <input
                                    type="number" min="0" max="100"
                                    style={{ ...styles.inlineInput, width: '60px', textAlign: 'center' }}
                                    value={vatRatePercent}
                                    onChange={e => setVatRatePercent(Number(e.target.value) || 0)}
                                />
                                <span style={{ color: '#9ca3af' }}>%</span>
                            </span>
                            <span>{vatAmount.toFixed(2)} {getCurrencySymbol()}</span>
                        </div>
                        <div style={styles.grandTotalRow}>
                            <span style={styles.grandTotalLabel}>GENEL TOPLAM:</span>
                            <span style={styles.grandTotalValue}>{grandTotal.toFixed(2)} {getCurrencySymbol()}</span>
                        </div>
                    </div>
                </div>

                {/* Save */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        style={{ ...styles.saveButton, opacity: saving ? 0.7 : 1 }}
                        onMouseEnter={e => { if (!saving) { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 6px 20px rgba(16,185,129,0.4)'; } }}
                        onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 15px rgba(16,185,129,0.3)'; }}
                    >
                        {saving ? '⏳ Kaydediliyor...' : '💾 Teklifi Kaydet'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default NewPriceQuote;
